'use client';

import React, { useState, useEffect } from 'react';
import { useWindowDimensions } from '@/hooks/general/useWindowDimensions/useWindowDimensions';
import { useAccountStore } from '@/store/accountStore';
import { MemoizedMobileLangSwitcher } from '@/components/blocks/header/MobileLangSwitcher/MobileLangSwitcher';
import { Box, Button, Stack, IconButton } from '@mui/material';
import { HEADER_PHRASES } from '@/consts/phrases/headerPhrases';
import { IPhrases } from '@/types/types/page/pages';
import { usePropsPhrases } from '@/utils/phrases/usePropsPhrases';
import { ScreenType } from '@/types/enums/media';
import { localeDict } from '@/consts/languageAccordance';
import { MenuIcon } from '@/components/blocks/header/MenuIcon/MenuIcon';
import { MemoizedMobileMenu } from '@/components/blocks/header/MobileMenu/MobileMenu';
import { MemoizedTopMenu } from '@/components/blocks/header/TopMenu/TopMenu';
import { useHydratedAuthState } from '@/hooks/general/useHydratedAuthState/useHydratedAuthState';
import { useLogout } from '@/hooks/redirects/useLogout/useLogout';
import { useLocale } from '@/hooks/general/useLocale/useLocale';
import { useIsLangSwitcherDisabled } from '@/hooks/helpers/useIsLangSwitcherDisabled/useIsLangSwitcherDisabled';
import {
    FlagWithCountryName,
    getCurrentLocaleParams,
} from '@/components/blocks/header/LangSwitcher/LangSwitcher';
import { Icon } from '@/components/general/Icon/Icon';
import Link from '@/components/general/Link/Link';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import topmenuStyles from '@/styles/modules/TopMenu.module.css';
import headerStyles from '@/styles/modules/Header.module.css';

interface HeaderProps {
    phrases?: IPhrases;
}

export const Header = ({ phrases }: HeaderProps) => {
    const { t } = usePropsPhrases(phrases);
    const i18nLocale = useLocale();
    const isLangSwitcherDisabled = useIsLangSwitcherDisabled();

    const { handleLogout } = useLogout();
    const { isLoggedIn } = useHydratedAuthState();

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [localesMenuOpen, setLocalesMenuOpen] = useState(false);

    const faqUrl = t(HEADER_PHRASES.FAQ_URL, { asString: true });
    const helpCentreUrl = t(HEADER_PHRASES.HELP_CENTRE_URL, { asString: true });
    const blogUrl = t(HEADER_PHRASES.BLOG_URL, { asString: true });
    const contactUrl = t(HEADER_PHRASES.CONTACT_US_URL, { asString: true });

    const {
        surveys,
        referrals,
        hasProfileQuestions,
        isEmailConfirmed,
        isVerificationRequired,
        walletBalance,
        isTaxableHonorariaThreshold,
    } = useAccountStore();

    const badgeDetails = JSON.stringify({
        surveyQty: surveys,
        referralQty: referrals,
        hasProfileIcon: hasProfileQuestions,
        hasAccountIcon:
            isVerificationRequired || !isEmailConfirmed || !!isTaxableHonorariaThreshold,
        hasWalletIcon: !!walletBalance,
        hasTaxDetailsIcon: !!isTaxableHonorariaThreshold,
    });

    const { screenType } = useWindowDimensions();

    const isMobile = screenType === ScreenType.Tablet || screenType === ScreenType.Mobile;
    const menuOpen = mobileMenuOpen || localesMenuOpen;
    const hideHeaderComponent = isMobile && menuOpen;

    useEffect(() => {
        const HeaderElement = document?.querySelector('header');
        HeaderElement?.classList.toggle(headerStyles.headerActive, menuOpen);
    }, [menuOpen]);

    const closeMobileAndLocalesMenu = () => {
        setMobileMenuOpen(false);
        setLocalesMenuOpen(false);
    };

    const handleMobileMenuIconClick = () => {
        setMobileMenuOpen(!mobileMenuOpen);
        setLocalesMenuOpen(false);
    };

    const handleMobileMenuItemClick = () => {
        closeMobileAndLocalesMenu();
    };

    const handleMobileCloseMenuButtonClick = () => {
        closeMobileAndLocalesMenu();
    };

    const handleLocalesMenuIconClick = () => {
        setLocalesMenuOpen(!localesMenuOpen);
        setMobileMenuOpen(false);
    };

    const { currentCountry, currentCountryName, currentLanguageName } =
        getCurrentLocaleParams(i18nLocale);

    const localesToShow = isLoggedIn
        ? i18nLocale.locales?.filter((locale) =>
              locale.endsWith(`-${localeDict[i18nLocale.locale].country.code}`),
          )
        : i18nLocale.locales;

    const isMobileLangSwitcherVisible = localesToShow && localesToShow.length > 1;

    return (
        <>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <MemoizedMobileLangSwitcher
                    open={localesMenuOpen}
                    handleClose={() => setLocalesMenuOpen(false)}
                />
                <Stack
                    className={topmenuStyles.topmenu}
                    sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        position: 'relative',
                        cursor: 'pointer',
                    }}
                >
                    {!hideHeaderComponent && (
                        <Button
                            className="mobileLocaleButton language-button"
                            color={localesMenuOpen ? 'primary' : 'inherit'}
                            onClick={handleLocalesMenuIconClick}
                            data-testid="mobile-locale-button"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            disabled={!isMobileLangSwitcherVisible || isLangSwitcherDisabled}
                        >
                            <FlagWithCountryName
                                countryCode={currentCountry.code}
                                countryName={currentCountryName}
                                languageName={currentLanguageName}
                            />
                            {isMobileLangSwitcherVisible && !isLangSwitcherDisabled && (
                                <ArrowDropDownIcon sx={{ mr: -1 }} />
                            )}
                        </Button>
                    )}
                    {isLoggedIn && !hideHeaderComponent && (
                        <Button
                            onClick={handleLogout}
                            variant="outlined"
                            className={`mobileLogoutButton ${topmenuStyles.topmenuButton}`}
                            size="small"
                        >
                            <Icon
                                path="general/navigationIcons/LogoutIcon"
                                className={`${topmenuStyles.topmenuButtonIcon} topmenuButtonIcon`}
                            />
                            {t(HEADER_PHRASES.LOGOUT)}
                        </Button>
                    )}
                    {!isLoggedIn && !hideHeaderComponent && (
                        <Link href="/login" passHref className={topmenuStyles.topmenuButtonLink}>
                            <Button
                                variant="outlined"
                                className={`mobileLoginButton ${topmenuStyles.topmenuButton}`}
                            >
                                <Icon
                                    path="general/navigationIcons/LoginIcon"
                                    className={`${topmenuStyles.topmenuButtonIcon} topmenuButtonIcon`}
                                />
                                {t(HEADER_PHRASES.LOGIN)}
                            </Button>
                        </Link>
                    )}
                    {!hideHeaderComponent && (
                        <IconButton
                            aria-label="open menu"
                            className="mobileMenuButton"
                            onClick={handleMobileMenuIconClick}
                            color="primary"
                            sx={{ width: '24px', height: '24px', ml: 2 }}
                        >
                            <MenuIcon active={mobileMenuOpen} />
                        </IconButton>
                    )}
                    {hideHeaderComponent && (
                        <IconButton
                            aria-label="close menu"
                            className="mobileCloseMenuButton"
                            onClick={handleMobileCloseMenuButtonClick}
                            color="primary"
                            sx={{ width: '24px', height: '24px', ml: 2 }}
                        >
                            <MenuIcon active={true} />
                        </IconButton>
                    )}
                </Stack>
                <MemoizedMobileMenu
                    phrases={phrases}
                    open={mobileMenuOpen}
                    onItemClick={handleMobileMenuItemClick}
                    isLoggedIn={isLoggedIn}
                    blogUrl={blogUrl}
                    faqUrl={faqUrl}
                    contactUrl={contactUrl}
                    helpCentreUrl={helpCentreUrl}
                    badgeDetails={badgeDetails}
                />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <MemoizedTopMenu
                    phrases={phrases}
                    isLoggedIn={isLoggedIn}
                    handleLogout={handleLogout}
                    blogUrl={blogUrl}
                    faqUrl={faqUrl}
                    contactUrl={contactUrl}
                    helpCentreUrl={helpCentreUrl}
                    badgeDetails={badgeDetails}
                />
            </Box>
        </>
    );
};
